/**
 * The pre-chat form: turning `widget_prechat_field` ROWS into something a visitor
 * can fill in, and deciding what counts as filled in.
 *
 * WHAT THIS FILE IS NOT. It is not the validator. The API generates a zod schema
 * from the SAME rows at request time (E9, gate G-57), and that one is the
 * authority: it runs where the caller cannot reach it, and it is what the
 * database write is protected by. The checks here exist so the visitor finds out
 * about an empty required field before a round trip.
 *
 * The rules are therefore deliberately WEAKER than the server's and must stay
 * that way. A client check stricter than the server's rejects input the product
 * would have accepted, and the visitor has no way to discover why — they just
 * cannot submit. If the two ever disagree, this is the one that is wrong.
 */
import {
  KNOWN_PRECHAT_TYPES,
  type KnownPrechatType,
  type PrechatAnswer,
  type WidgetPrechatField,
} from '../api/contract';
import type { WidgetMessageId } from '../i18n/messages';

/**
 * The form's state. Strings even for a field whose answer is posted as a boolean:
 * a form control holds a string, and the conversion happens once, in
 * {@link prechatPayload}, rather than at every keystroke.
 */
export type PrechatValues = Readonly<Record<string, string>>;

/**
 * Every error is a message id, so a field cannot fail with copy that was never
 * translated. There is no `custom` case on purpose: a server-authored message
 * would arrive in one language and this form renders in two.
 */
export type FieldError = Extract<
  WidgetMessageId,
  | 'widget.validation.required'
  | 'widget.validation.email'
  | 'widget.validation.too_long'
  | 'widget.validation.choose'
>;

/**
 * The API bounds a pre-chat answer at 500 characters
 * (`prechatAnswerSchema`). The rows carry no per-field length, so this is the
 * only limit there is, and it is mirrored here so the visitor is told before the
 * round trip rather than by a 400.
 */
export const PRECHAT_ANSWER_MAX_LENGTH = 500;

/**
 * How to DRAW a field whose `type` the API sent.
 *
 * An unknown type becomes a text input rather than nothing. The alternative —
 * skipping what we do not recognise — silently removes a field the brand marked
 * required, and produces a form that cannot be submitted and cannot be debugged
 * from the visitor's side. The wire type is an open string precisely so that a
 * widget cached in someone's CDN keeps working when a type is added.
 */
export function renderType(type: string): KnownPrechatType {
  return (KNOWN_PRECHAT_TYPES as readonly string[]).includes(type)
    ? (type as KnownPrechatType)
    : 'text';
}

/** Rows arrive ordered by `position`; sorting here means a caller cannot forget to. */
export function orderedFields(
  fields: readonly WidgetPrechatField[],
): readonly WidgetPrechatField[] {
  return [...fields].sort((left, right) => left.position - right.position);
}

/**
 * The loosest check that still catches the mistake it is for: a missing `@`, a
 * missing dot, a stray space.
 *
 * Deliberately not an RFC 5322 pattern. Those reject addresses that are legal and
 * deliverable, and the only way a visitor experiences that is as a form that will
 * not let them talk to support. The server does the real check; the only thing
 * worth catching here is the typo the visitor can see for themselves.
 */
const EMAIL_SHAPE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/u;

export function validateField(field: WidgetPrechatField, raw: string): FieldError | null {
  const value = raw.trim();

  if (value === '') {
    // An optional field left blank is valid and is not checked any further —
    // running a format check over an empty optional email is how "optional"
    // becomes "optional unless you look at it".
    return field.required ? 'widget.validation.required' : null;
  }

  if (value.length > PRECHAT_ANSWER_MAX_LENGTH) {
    return 'widget.validation.too_long';
  }

  const type = renderType(field.type);

  if (type === 'email' && !EMAIL_SHAPE.test(value)) {
    return 'widget.validation.email';
  }

  // `options` is a list of strings — the value IS the label. That is the shape
  // the rows have, and inventing a {value,label} pair here would be this app
  // holding an opinion the database does not.
  if (type === 'select' && !field.options.includes(value)) {
    return 'widget.validation.choose';
  }

  return null;
}

/** Every field's error, keyed the same way the values are. Empty means valid. */
export function validatePrechat(
  fields: readonly WidgetPrechatField[],
  values: PrechatValues,
): Readonly<Record<string, FieldError>> {
  const errors: Record<string, FieldError> = {};
  for (const field of fields) {
    const error = validateField(field, values[field.key] ?? '');
    if (error !== null) {
      errors[field.key] = error;
    }
  }
  return errors;
}

/** A blank answer for every field, so no input starts uncontrolled. */
export function initialPrechatValues(fields: readonly WidgetPrechatField[]): PrechatValues {
  const values: Record<string, string> = {};
  for (const field of fields) {
    values[field.key] = '';
  }
  return values;
}

/**
 * What actually gets posted.
 *
 * Trimmed, and blanks dropped. Sending `{"phone": ""}` for an optional field the
 * visitor skipped writes an empty string into a person record, and an empty
 * string is not the same as "we do not know" — it is a value that silently
 * outranks the real phone number the next import would have supplied.
 *
 * Keys not backed by a field are dropped too: `values` is component state that
 * survives the form re-rendering with a different field list, and only the rows
 * the API sent may be echoed back to it.
 */
export function prechatPayload(
  fields: readonly WidgetPrechatField[],
  values: PrechatValues,
): Readonly<Record<string, PrechatAnswer>> {
  const payload: Record<string, PrechatAnswer> = {};
  for (const field of fields) {
    const value = (values[field.key] ?? '').trim();
    if (value !== '') {
      payload[field.key] = value;
    }
  }
  return payload;
}
